// Worked example: $15,000 raised from 300 × $50 donations; 80% of donors
// cover Square's fee, so the school pays processing on 60 donations. The
// PagePledge side uses the starting campaign fee from the price list.
const RAISED = 15_000;
const SCHOOL_PROCESSING = 102;
const COMPETITOR_FEE = 3_000; // 20% including processing

export default function FeeComparison({ startingPriceCents }) {
  const campaignFee = Math.round(startingPriceCents / 100);
  const schoolPays = campaignFee + SCHOOL_PROCESSING;
  const schoolKeeps = RAISED - schoolPays;
  const difference = schoolKeeps - (RAISED - COMPETITOR_FEE);
  const usd = (dollars) => `$${dollars.toLocaleString("en-CA")}`;

  return (
    <div className="max-w-4xl mx-auto space-y-7">
      <div className="space-y-3">
        <h2 className="text-3xl md:text-4xl leading-tight">Keep more of what your school raises.</h2>
        <p className="text-muted-foreground leading-relaxed">
          Imagine your school raises $15,000 from 300 donations of $50.
          On PagePledge, 240 donors choose to cover processing fees.
          The school pays just $102 in processing fees on the remaining 60 donations.
          This example uses PagePledge’s {usd(campaignFee)} starting campaign fee.
        </p>
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-4 border-y border-border py-4">
        <ExampleStat value="$15,000" label="raised for the school" />
        <ExampleStat value="300" label="donations of $50" />
        <ExampleStat value="80%" label="cover processing fees" />
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Square charges $522 in total: the 240 donors covering fees add
        $1.75 each ($420), and the school pays $1.70 on each of the
        remaining 60 donations ($102).
      </p>
      <div className="space-y-7">
        <div className="overflow-hidden rounded-xl border border-accent/20 bg-card">
          <table className="w-full table-fixed text-xs sm:text-sm text-left leading-relaxed break-words">
            <caption className="sr-only">PagePledge compared with a competitor charging 20% for a $15,000 fundraiser</caption>
            <colgroup><col className="w-[28%]" /><col className="w-[36%]" /><col className="w-[36%]" /></colgroup>
            <thead>
              <tr className="border-b border-accent/20">
                <th scope="col" className="p-2 sm:p-4 font-medium text-muted-foreground">Compare</th>
                <th scope="col" className="p-2 sm:p-4 bg-accent/10 text-accent font-semibold text-sm sm:text-lg">PagePledge</th>
                <th scope="col" className="p-2 sm:p-4">
                  <span className="block text-sm sm:text-lg font-semibold">Other competitors</span>
                  <span className="block mt-1 font-normal text-muted-foreground">20% fee example</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <TableRow label="Campaign pricing" ours={`From ${usd(campaignFee)}. One flat fee based on student count.`} theirs="20% of donations in this example" />
              <TableRow label="As you raise more" ours="Your campaign fee stays the same" theirs="20¢ of each extra dollar goes to fees" />
              <TableRow label="School pays"
                ours={<><strong className="text-accent text-sm sm:text-xl">{usd(schoolPays)}</strong><span className="block mt-1">{usd(campaignFee)} campaign fee + {usd(SCHOOL_PROCESSING)} processing</span></>}
                theirs={<><strong className="text-sm sm:text-xl">$3,000</strong><span className="block mt-1">20% including processing</span></>} />
              <TableRow label="School keeps"
                ours={<><strong className="text-accent text-sm sm:text-xl tabular-nums">{usd(schoolKeeps)}</strong><span className="block mt-1">after campaign and processing fees</span></>}
                theirs={<><strong className="text-sm sm:text-xl tabular-nums">$12,000</strong><span className="block mt-1">after the 20% fee</span></>} />
            </tbody>
          </table>
        </div>
        <div className="rounded-xl bg-accent px-5 py-6 text-accent-foreground">
          <p className="font-display text-3xl md:text-4xl">{usd(difference)} more for your school.</p>
          <p className="mt-2 text-sm leading-relaxed">That’s the difference in this example, after PagePledge’s {usd(campaignFee)} campaign fee and the school’s share of processing fees.</p>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Example assumptions: Square’s standard Canadian online rate of 2.8% + 30¢ and 80% of donors covering processing fees on PagePledge.
        Those donors pay an extra $1.75 each, separate from the $15,000 raised.
        The PagePledge example uses the {usd(campaignFee)} starting tier; your campaign fee depends on student count.
        The competitor example assumes a 20% total fee including processing. Competitor pricing and donor choices vary.
      </p>
    </div>
  );
}

function ExampleStat({ value, label }) {
  return <div><p className="font-display text-2xl text-foreground">{value}</p><p className="text-xs text-muted-foreground">{label}</p></div>;
}

function TableRow({ label, ours, theirs }) {
  return (
    <tr className="border-b border-border/70 last:border-0 align-top">
      <th scope="row" className="p-2 sm:p-4 font-medium">{label}</th>
      <td className="p-2 sm:p-4 bg-accent/5">{ours}</td>
      <td className="p-2 sm:p-4 text-muted-foreground">{theirs}</td>
    </tr>
  );
}
